import api from './client';
import type { LLMModelCatalogItem } from './llm';

// 桌面端云端账号绑定状态（本地后端 /api/v1/cloud/*）
export interface CloudAccount {
  server_url: string;
  email: string;
  tier: string;
}

export interface CloudStatus {
  bound: boolean;
  account?: CloudAccount;
}

export interface CloudLoginPayload {
  server_url: string;
  email: string;
  password: string;
}

export const cloudApi = {
  // 查询云端账号绑定状态
  getStatus: () => api.get<CloudStatus>('/api/v1/cloud/status').then((r) => r.data),
  // 绑定云端账号（邮箱 + 密码）
  login: (data: CloudLoginPayload) => api.post('/api/v1/cloud/login', data).then((r) => r.data),
  // 解绑云端账号
  logout: () => api.post('/api/v1/cloud/logout').then((r) => r.data),
  // 支付完成后刷新会员 tier 缓存（否则最长等 10 分钟才解锁）
  refreshTier: () => api.post('/api/v1/cloud/refresh-tier').then((r) => r.data),
};

// ─── 付费上云：桌面端经 forward 代理在云端下单 ───
const fwd = (path: string) => `/api/v1/cloud-proxy/forward/${path}`;

export interface CloudPaymentOrder {
  order_id: string;
  amount: number;
  status: string;
  pay_url?: string;
  pay_qr_code?: string;
}

export const cloudBillingApi = {
  paymentMethods: () => api.get<{ id: string; name: string; enabled: boolean }[]>(fwd('billing/payment-methods')).then((r) => r.data),
  payment: (orderId: string) =>
    api.get<{ status: string }>(fwd(`billing/payments/${orderId}`)).then((r) => r.data),
  // 余额充值（桌面端经云端下单；本机无支付凭证不调本机接口）
  topup: (data: { amount: number; payment_method: string; coupon_code?: string; pay_params?: Record<string, any> }) =>
    api.post<CloudPaymentOrder>(fwd('billing/topup'), data).then((r) => r.data),
  validateCoupon: (data: Record<string, any>) =>
    api.post<{ final_amount: number; discount_amount: number }>(fwd('billing/validate-coupon'), data).then((r) => r.data),
  // 平台模型目录（云端实价，桌面端「平台模型」分组用）
  catalog: () => api.get<{ models: LLMModelCatalogItem[] }>(fwd('llm/models/catalog')).then((r) => r.data),
  // 云端余额（平台模型按量扣费用）
  balance: () => api.get<{ balance: number; frozen: number; total_deposited: number; total_used: number }>(fwd('billing/balance')).then((r) => r.data),
};

// ─── 云端数据同步（端到端加密版） ─────────────────────────────────────────────
// 数据流：客户端加密 → /sync/snapshot 密文上传 → 云端只存密文
//         客户端解密 ← /sync/snapshot/latest 密文下载 ← 云端取密文
// 同步口令永远不离开浏览器；服务端无法还原明文。

export interface EncryptedSnapshotData {
  salt: string;
  iv: string;
  ciphertext: string;
  entity_count: number;
  created_at: string | null;
}

export interface SnapshotMeta {
  id: string;
  size_bytes: number;
  entity_count: number;
  expires_at: string | null;
  created_at: string | null;
  op_seq: number | null;
}

export const cloudSyncApi = {
  /** 初始化同步（生成 salt）；若已有 salt 则直接返回。 */
  initSync: () => api.post<{ salt: string }>(fwd('sync/snapshot/init')).then((r) => r.data),

  /** 上传加密快照（密文整体 PUT，服务端不解密）。op_seq=快照覆盖到的 op 锚点（本机 op_cursor）。 */
  pushEncrypted: (payload: { salt: string; iv: string; ciphertext: string; entity_count: number; expires_days?: number; op_seq?: number }) =>
    api.post<{ success: boolean; id: string; size_bytes: number }>(fwd('sync/snapshot'), payload).then((r) => r.data),

  /** 拉取最新加密快照（返回密文，客户端自行解密）。 */
  pullEncrypted: () => api.get<EncryptedSnapshotData>(fwd('sync/snapshot/latest')).then((r) => r.data),

  /** 列出快照元信息（不含密文；按创建时间倒序，首个即最新）。 */
  listSnapshots: () => api.get<SnapshotMeta[]>(fwd('sync/snapshots')).then((r) => r.data),

  /** 列出已绑定云端的同步设备。 */
  listDevices: () => api.get<SyncDevice[]>(fwd('sync/devices')).then((r) => r.data),

  /** 吊销指定设备（吊销后该设备需重新绑定才能同步）。 */
  revokeDevice: (id: string) => api.delete<{ success: boolean }>(fwd(`sync/devices/${id}`)).then((r) => r.data),
};

export interface SyncDevice {
  id: string;
  name: string;
  fingerprint: string;
  last_seen_at: string | null;
  last_sync_at: string | null;
  created_at: string | null;
}
